/**
 * Schermate di stato e overlay di diagnostica. Testi in italiano, sfondo nero, font di sistema,
 * dimensioni in vmin per restare leggibili a 3 metri su qualunque risoluzione.
 * Sopra i contenuti non appare mai nulla, salvo queste schermate (API.md §4).
 */

const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, css: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.style.cssText = css;
  if (text !== undefined) e.textContent = text;
  return e;
}

export class Screens {
  private readonly layer: HTMLDivElement;

  constructor(root: HTMLElement) {
    this.layer = el('div', `position:absolute;inset:0;z-index:10;background:#000;color:#fff;font-family:${FONT};`
      + 'display:none;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:4vmin;box-sizing:border-box;');
    this.layer.dataset.testid = 'screen';
    root.appendChild(this.layer);
  }

  private show(children: HTMLElement[], kind: string): void {
    this.layer.replaceChildren(...children);
    this.layer.dataset.kind = kind;
    this.layer.style.display = 'flex';
  }

  hide(): void {
    this.layer.style.display = 'none';
    this.layer.replaceChildren();
    delete this.layer.dataset.kind;
  }

  get currentKind(): string | null {
    return this.layer.style.display === 'none' ? null : (this.layer.dataset.kind ?? null);
  }

  /** Schermata di abbinamento: codice enorme + istruzione. */
  showPairing(code: string | null, status: string, host = 'streamer.hooxlab.com'): void {
    const title = el('div', 'font-size:6vmin;font-weight:600;opacity:.9;margin-bottom:5vmin;', 'Abbina questo schermo');
    const codeEl = el('div', 'font-size:22vmin;font-weight:800;letter-spacing:.12em;line-height:1;margin:2vmin 0 6vmin;'
      + 'font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;padding-left:.12em;', code ?? '······');
    codeEl.dataset.testid = 'pairing-code';
    const how = el('div', 'font-size:4.2vmin;line-height:1.4;max-width:90vw;opacity:.95;',
      `Vai su ${host} → Schermi → Aggiungi schermo e inserisci il codice`);
    const st = el('div', 'font-size:3vmin;opacity:.6;margin-top:5vmin;', status);
    st.dataset.testid = 'pairing-status';
    this.show([title, codeEl, how, st], 'pairing');
  }

  /** Avvio fallito: messaggio d'errore e conto alla rovescia del nuovo tentativo. */
  showStartError(message: string, retryInS: number): void {
    const title = el('div', 'font-size:9vmin;font-weight:700;margin-bottom:3vmin;', 'Avvio non riuscito');
    const msg = el('div', 'font-size:3.2vmin;opacity:.85;max-width:90vw;word-break:break-word;margin-bottom:3vmin;', message);
    msg.dataset.testid = 'start-error';
    const again = el('div', 'font-size:3.6vmin;opacity:.7;', `Riprovo tra ${retryInS} s`);
    this.show([title, msg, again], 'start-error');
  }

  showDisabled(): void {
    this.show([
      el('div', 'font-size:9vmin;font-weight:700;margin-bottom:3vmin;', 'Schermo disattivato'),
      el('div', 'font-size:4vmin;opacity:.8;max-width:80vw;line-height:1.4;', 'Questo schermo è stato disattivato dal portale. Riproverò automaticamente.'),
    ], 'disabled');
  }

  showInactive(): void {
    this.show([
      el('div', 'font-size:9vmin;font-weight:700;margin-bottom:3vmin;', 'Abbonamento non attivo'),
      el('div', 'font-size:4vmin;opacity:.8;max-width:80vw;line-height:1.4;', 'Rinnova l\'abbonamento dal portale per riprendere la trasmissione. Riproverò automaticamente.'),
    ], 'inactive');
  }
}

export type DiagLines = Array<[string, string]>;

export class DiagOverlay {
  private readonly box: HTMLPreElement;
  private shown = false;

  constructor(root: HTMLElement) {
    this.box = el('pre', 'position:absolute;left:1.5vmin;top:1.5vmin;z-index:20;margin:0;padding:1.5vmin 2vmin;'
      + 'background:rgba(0,0,0,.78);color:#9f9;font:2.1vmin/1.35 ui-monospace,Menlo,Consolas,monospace;'
      + 'border:1px solid #3a3;border-radius:6px;max-width:70vw;white-space:pre-wrap;word-break:break-all;display:none;pointer-events:none;');
    this.box.dataset.testid = 'diag';
    root.appendChild(this.box);
  }

  get visible(): boolean { return this.shown; }

  set(visible: boolean): void {
    this.shown = visible;
    this.box.style.display = visible ? 'block' : 'none';
  }

  toggle(): void { this.set(!this.shown); }

  update(lines: DiagLines): void {
    if (!this.shown) return;
    const w = Math.max(...lines.map(([k]) => k.length));
    this.box.textContent = lines.map(([k, v]) => `${k.padEnd(w)}  ${v}`).join('\n');
  }
}
